import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CancelButton,
  CloseMarkCont,
  CloseMarkOnCancelCont,
  CreateButton,
  SurveyModalBlackBG,
  SurveyModalBody,
  SurveyModalCont,
  SurveyModalFooter,
  SurveyModalHeader,
} from './CreateSurveyModal.styles';
import { RouteNames } from '../../constans/routes';
import { CreateSurveyCardsNames, CreateSurveyTypeNames } from '../../constans/state';
import { useOrganizations, useToast } from '../../hooks';
import { useAuth } from '../../hooks/useAuth';
import { ISurveyData, useSurveyModal } from '../../hooks/useSurveyModal';
import { IOrganizations, ProjectEntity } from '../../services/organisations';
import { HeadingExtraBoldS } from '../../styles/globals';
import CreateNewSurveyForm from '../CreateNewSurveyForm/CreateNewSurveyForm';
import Spinner from '../Spinner/Spinner';
import CloseMark from '@/assets/icons/closeMark.svg?react';

interface ICreateSurveyModal {
  active: boolean;
  closeModal: () => void;
  refreshList?: () => void;
}

export interface ICreateSurveyApiError {
  survey?: string;
  lang?: string;
  name?: string;
  organisation?: string;
}

const defaultApiErrorValues = {
  survey: '',
  lang: '',
  name: '',
  organisation: '',
};

const CreateSurveyModal: FC<ICreateSurveyModal> = ({ active, closeModal, refreshList }) => {
  const [allOrganizations, setAllOrganizations] = useState<IOrganizations[]>([]);
  const [allProjects, setAllProjects] = useState<ProjectEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const { organizations, getProjects } = useOrganizations();
  const [apiError, setApiError] = useState<ICreateSurveyApiError>(defaultApiErrorValues);
  const [organisationError, setOrganisationError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createSurvey } = useSurveyModal();
  const { isEBHRMember, userRolesFromToken } = useAuth();
  const isLongForm = isEBHRMember;

  useEffect(() => {
    if (isLongForm) {
      setAllOrganizations(organizations);
    } else {
      getProjects(Number(userRolesFromToken.organizationId)).then(setAllProjects);
    }
  }, [isLongForm, organizations]);

  const submitForm = () => {
    const submitButton: HTMLElement | null = document.getElementById('submitCreateSurveyForm');
    if (submitButton) {
      submitButton.click();
    }
  };

  const closeModalForm = () => {
    if (!loading) {
      closeModal();
      setApiError(defaultApiErrorValues);
    }
  };

  const createSurveyAndRefresh = async (surveyData: ISurveyData) => {
    try {
      setLoading(true);
      const organizationId =
        allOrganizations.find(organization => organization.name === surveyData.organization)?.id ||
        undefined;
      if (isLongForm && !organizationId) {
        setOrganisationError(t('createSurveyModal.form.validation.organization'));
        setLoading(false);
      } else {
        const create = await createSurvey({
          ...surveyData,
          organizationId: organizationId,
          copyFrom: Number(surveyData.copyFrom),
        });
        if (create) {
          if (refreshList) {
            refreshList();
          }
          navigate(
            `${RouteNames.CreateSurvey}/${create.id}?card=${CreateSurveyCardsNames.contents}${
              surveyData.survey === 'EFFECT' ? `&domain=${CreateSurveyTypeNames.internal}` : ''
            }`
          );
          setLoading(false);
          return closeModalForm();
        }
      }
    } catch ({ data }) {
      const translatedApiErrors: any = { ...defaultApiErrorValues };

      if (data?.details) {
        Object.entries(data.details).forEach(([field, messages]) => {
          if (field === 'name') {
            translatedApiErrors.name = [t('viewSurvey.contents.notifications.unique.description')];
          } else {
            translatedApiErrors[field as keyof ICreateSurveyApiError] = messages;
          }
        });
      }

      setApiError(translatedApiErrors);
      setLoading(false);
      return;
    }
  };

  return (
    <>
      {active && (
        <>
          <SurveyModalBlackBG onClick={closeModalForm} data-testid="modalBlackOpacityBg" />
          <SurveyModalCont data-testid="surveyModalContainer">
            <SurveyModalHeader>
              <HeadingExtraBoldS>{t('createSurveyModal.heading')}</HeadingExtraBoldS>
              <CloseMarkCont data-testid="closeMarkOnModal" onClick={closeModalForm}>
                <CloseMark />
              </CloseMarkCont>
            </SurveyModalHeader>
            <SurveyModalBody>
              {/* <CreateSurveyForm
                submit={createSurveyAndRefresh}
                longForm={isLongForm}
                apiError={apiError}
                organisationError={{ error: organisationError, set: setOrganisationError }}
                allOrganizations={allOrganizations}
              /> */}
              <CreateNewSurveyForm
                submit={createSurveyAndRefresh}
                apiError={apiError}
                // TODO: apiError should have access configuration for the allowed surveys
                organizationError={{ error: organisationError, set: setOrganisationError }}
                allOrganizations={allOrganizations}
                allProjects={allProjects}
                setAllProjects={setAllProjects}
                getProjects={getProjects}
              />
            </SurveyModalBody>
            <SurveyModalFooter>
              <CreateButton
                data-testid="createSurveyButton"
                onClick={submitForm}
                disabled={loading}
              >
                {loading ? <Spinner /> : t('createSurveyModal.create')}
              </CreateButton>
              <CancelButton data-testid="cancelSurveyButton" onClick={closeModalForm}>
                <CloseMarkOnCancelCont>
                  <CloseMark />
                </CloseMarkOnCancelCont>
                {t('createSurveyModal.cancel')}
              </CancelButton>
            </SurveyModalFooter>
          </SurveyModalCont>
        </>
      )}
    </>
  );
};

export default CreateSurveyModal;
